import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Award } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

// Sprint A6.1 — Give-a-Recognition dialog (Client Admin + Evaluated user surfaces).
// Permission gates are enforced server-side; UI hides options the caller cannot pick.

const CATEGORIES = [
  'teamwork', 'innovation', 'leadership', 'client_impact',
  'ownership', 'kindness', 'above_beyond',
];

export function RecognitionDialog({ open, onClose, currentUser, onCreated }) {
  const { t } = useTranslation();
  const role = currentUser?.role || '';
  const isHr = role === 'client_admin' || role === 'client_hr';
  const isAdmin = role === 'client_admin';

  const [collabs, setCollabs] = useState([]);
  const [toUserId, setToUserId] = useState('');
  const [type, setType] = useState('peer');
  const [category, setCategory] = useState('teamwork');
  const [visibility, setVisibility] = useState('public');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setToUserId(''); setType('peer'); setCategory('teamwork');
    setVisibility('public'); setMessage(''); setSearch('');
    clientApi.listCollaborators()
      .then((c) => setCollabs(Array.isArray(c) ? c : []))
      .catch(() => setCollabs([]));
  }, [open]);

  // UAT — accent-insensitive, token-based search ("Victor Alvarez" must match
  // "Víctor Raúl Álvarez").
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const filteredCollabs = useMemo(() => {
    const tokens = norm(search).split(/\s+/).filter(Boolean);
    const others = collabs.filter((c) => c.id !== currentUser?.id);
    if (!tokens.length) return others;
    return others.filter((c) => {
      const hay = `${norm(c.name)} ${norm(c.email)}`;
      return tokens.every((tk) => hay.includes(tk));
    });
  }, [collabs, search, currentUser]);
  const searchMatchesSelf = useMemo(() => {
    const tokens = norm(search).split(/\s+/).filter(Boolean);
    if (!tokens.length || !currentUser) return false;
    const hay = `${norm(currentUser.name)} ${norm(currentUser.email)}`;
    return tokens.every((tk) => hay.includes(tk));
  }, [search, currentUser]);

  const submit = async () => {
    if (!toUserId) { toast.error(t('recognitionDialog.errMissingRecipient')); return; }
    if (message.trim().length < 2) { toast.error(t('recognitionDialog.errMessageTooShort')); return; }
    setSubmitting(true);
    try {
      await clientApi.createRecognition({
        to_user_id: toUserId, type, category, message: message.trim(), visibility,
      });
      toast.success(t('recognitionDialog.created'));
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(t('recognitionDialog.errCreate'), { description: formatApiError(e) });
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-lg" data-testid="recognition-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-warning" />
            {t('recognitionDialog.title')}
          </DialogTitle>
          <DialogDescription>{t('recognitionDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('recognitionDialog.searchLabel')}</Label>
            <Input
              data-testid="rec-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('recognitionDialog.searchPlaceholder')}
            />
            <div className="max-h-40 overflow-y-auto rounded-md border" data-testid="rec-recipient-list">
              {filteredCollabs.length === 0 ? (
                <p className="text-xs text-muted-foreground p-3" data-testid="rec-empty-hint">
                  {searchMatchesSelf
                    ? t('recognitionDialog.selfExcluded', { defaultValue: 'You cannot send a recognition to yourself.' })
                    : t('recognitionDialog.noCollabs')}
                </p>
              ) : (
                filteredCollabs.slice(0, 30).map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setToUserId(c.id)}
                    data-testid={`rec-recipient-${c.id}`}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted/60 ${toUserId === c.id ? 'bg-muted' : ''}`}
                  >
                    <span className="font-medium truncate">{c.name || c.email}</span>
                    {c.department_name && (
                      <span className="text-xs text-muted-foreground truncate ml-2">{c.department_name}</span>
                    )}
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t('recognitionDialog.type')}</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="rec-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="peer">{t('recognitionDialog.typePeer')}</SelectItem>
                  {isHr && <SelectItem value="manager">{t('recognitionDialog.typeManager')}</SelectItem>}
                  {isAdmin && <SelectItem value="company">{t('recognitionDialog.typeCompany')}</SelectItem>}
                </SelectContent>
              </Select>
              {/* UAT — make the recognition-type policy explicit */}
              <p className="text-xs text-muted-foreground" data-testid="rec-type-hint">
                {type === 'peer' && t('recognitionDialog.hintPeer', { defaultValue: 'Peer: any collaborator can recognize a colleague.' })}
                {type === 'manager' && t('recognitionDialog.hintManager', { defaultValue: 'Manager: sent on behalf of leadership (HR / admins only).' })}
                {type === 'company' && t('recognitionDialog.hintCompany', { defaultValue: 'Company: official organization-wide recognition (admin only).' })}
              </p>
            </div>
            <div className="space-y-2">
              <Label>{t('recognitionDialog.category')}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="rec-category-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>{t(`recognitionDialog.categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('recognitionDialog.visibility')}</Label>
            <Select value={visibility} onValueChange={setVisibility}>
              <SelectTrigger data-testid="rec-visibility-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="public">{t('recognitionDialog.visibilityPublic')}</SelectItem>
                <SelectItem value="managers_only">{t('recognitionDialog.visibilityManagers')}</SelectItem>
                <SelectItem value="private">{t('recognitionDialog.visibilityPrivate')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t('recognitionDialog.message')}</Label>
            <Textarea
              data-testid="rec-message-textarea"
              maxLength={500}
              rows={3}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('recognitionDialog.messagePlaceholder')}
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/500</p>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-col items-stretch gap-2">
          {(!toUserId || message.trim().length < 2) && (
            <p className="text-xs text-muted-foreground text-right" data-testid="rec-disabled-reason">
              {!toUserId
                ? t('recognitionDialog.needRecipient', { defaultValue: 'Select a recipient to enable sending.' })
                : t('recognitionDialog.needMessage', { defaultValue: 'Write a short message (at least 2 characters) to enable sending.' })}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="rec-cancel">
              {t('common.cancel')}
            </Button>
            <Button onClick={submit} disabled={submitting || !toUserId || message.trim().length < 2} data-testid="rec-submit">
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('recognitionDialog.submit')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default RecognitionDialog;
